import React from 'react'
import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import './About.css'
import adultImage from '../assets/images/adult.jpg'

const About = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const features = [
    {
      icon: "❤️",
      title: "Health Guaranteed",
      description: "All our puppies come with comprehensive health guarantees and are vet-checked."
    },
    {
      icon: "🏆",
      title: "Champion Bloodlines",
      description: "Our breeding program features only the finest pedigreed champion lines."
    },
    {
      icon: "👨‍👩‍👧‍👦",
      title: "Family Socialized",
      description: "Each puppy is raised in our family environment with children and other pets."
    },
    {
      icon: "🛡️",
      title: "Professional Training",
      description: "Basic training and socialization foundation provided from birth."
    },
    {
      icon: "📋",
      title: "Complete Documentation",
      description: "Full registration papers, health records, and pedigree certificates included."
    },
    {
      icon: "🤝",
      title: "Lifetime Support",
      description: "We're here for you and your Cane Corso throughout their entire life."
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }

  return (
    <section id="about" className="about" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2>About Majestic cane corso puppies</h2>
          <p>
            At majesticcorsokennels.com, we are dedicated to breeding exceptional Cane Corso puppies
            with superior temperament, health, and conformation. Our passion for this magnificent breed
            drives everything we do.
          </p>
        </motion.div>

        <div className="about-content">
          <motion.div
            className="about-text"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="text-content">
              <h3>Our Commitment to Excellence</h3>
              <p>
                We are passionate breeders dedicated to preserving the magnificent Cane Corso breed. 
                Our commitment goes beyond just breeding; we focus on creating companions that will 
                enrich families for years to come. Each puppy is raised with love, proper socialization, 
                and comprehensive health testing to ensure they become wonderful family guardians.
              </p>
              <p>
                With over 15 years of experience in breeding Cane Corsos, we understand the importance 
                of temperament, health, and proper socialization. Our breeding program carefully selects 
                only the finest bloodlines, ensuring that each puppy inherits the best traits of this 
                noble breed.
              </p>
              
              <motion.div 
                className="experience-stats"
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
              >
                <motion.div className="stat" variants={itemVariants}>
                  <span className="stat-number">15+</span>
                  <span className="stat-label">Years Experience</span>
                </motion.div>
                <motion.div className="stat" variants={itemVariants}>
                  <span className="stat-number">200+</span>
                  <span className="stat-label">Happy Families</span>
                </motion.div>
                <motion.div className="stat" variants={itemVariants}>
                  <span className="stat-number">5⭐</span>
                  <span className="stat-label">Customer Rating</span>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            className="about-image"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="image-container">
              <img 
                src={adultImage} 
                alt="Adult Cane Corso" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <motion.div 
                className="image-overlay"
                whileHover={{ opacity: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <span>Our Beautiful Adults</span>
              </motion.div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="features-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <h3 className="features-title">Why Choose majesticcorsokennels.com?</h3>
          <div className="features">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.05, 
                  y: -5,
                  transition: { duration: 0.2 }
                }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h4 className="feature-title">{feature.title}</h4>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default About